/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluModalScreen, CluProjectCreationSteps, CluProjectDto } from 'src/clu/verticals/cluProjectCreationModal/CluProjectCreationModal';
import { CluModalFooter } from 'src/clu/verticals/cluProjectCreationModal/components/CluModalFooter';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';

export type ProjectCreationReviewAndFinishProps = {
    isLoading: boolean;
    onSubmit: () => void;
    onDismiss: () => void;
    onPrevious: () => void;
    projectDto: CluProjectDto;
};

const messages = defineMessages({
    title: {
        id: 'customText.reviewAndFinishScreen.title',
        defaultMessage: 'Review and finish'
    },
    description: {
        id: 'customText.reviewAndFinishScreen.description',
        defaultMessage: 'Review the configurations you set for your project in the previous steps.'
    },
    projectType: {
        id: 'customText.reviewAndFinishScreen.projectType',
        defaultMessage: 'Project type'
    },
    azureResource: {
        id: 'customText.reviewAndFinishScreen.azureResource',
        defaultMessage: 'Azure resource'
    },
    projectName: {
        id: 'customText.reviewAndFinishScreen.projectName',
        defaultMessage: 'Project name'
    },
    projectDescription: {
        id: 'customText.reviewAndFinishScreen.projectDescription',
        defaultMessage: 'Description'
    },
    language: {
        id: 'customText.reviewAndFinishScreen.language',
        defaultMessage: 'Language'
    },
    multipleLanguages: {
        id: 'customText.reviewAndFinishScreen.multipleLanguages',
        defaultMessage: 'Multiple languages'
    },
    enabled: {
        id: 'customText.reviewAndFinishScreen.enabled',
        defaultMessage: 'Enabled'
    },
    disabled: {
        id: 'customText.reviewAndFinishScreen.disabled',
        defaultMessage: 'Disabled'
    },
    noValuePlaceholder: {
        id: 'customText.reviewAndFinishScreen.noValuePlaceholder',
        defaultMessage: '-'
    },
    conversationProjectType: {
        id: 'customText.reviewAndFinishScreen.conversationProjectType',
        defaultMessage: 'Conversation'
    },
    orchestrationProjectType: {
        id: 'customText.reviewAndFinishScreen.orchestrationProjectType',
        defaultMessage: 'Orchestration Workflow'
    }
});

const LabeledText = ({ label, text }: { label: string; text: string }) => (
    <Stack tokens={{ childrenGap: 4 }}>
        <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{label}</LuisText>
        <LuisText>{text}</LuisText>
    </Stack>
);

export const CluFinishScreen = observer((props: ProjectCreationReviewAndFinishProps) => {
    const { isLoading, projectDto, onSubmit, onDismiss, onPrevious } = props;
    const { localize } = useLocalization();
    const { description, name, isMultiLingual, culture, projectType } = projectDto;
    const { rootStore } = useGlobalState();
    const { csAccountStore } = rootStore.azureStore.session;
    const projectCreationTracker = useProgressTracker({ onComplete: onSubmit });

    const cultureName = (projectType === 'Conversation'
        ? rootStore.cluConversationCultureStoreHouse
        : rootStore.cluOrchestrationCultureStoreHouse
    ).items.find(l => l.code === culture).name;
    const multiLinguality = isMultiLingual ? localize(messages.enabled) : localize(messages.disabled);

    return (
        <>
            <CluModalScreen tokens={{ childrenGap: 30 }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText as="h2" variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>

                    <LuisText>{localize(messages.description)}</LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 15 }}>
                    <LabeledText label={localize(messages.azureResource)} text={csAccountStore.name} />

                    <LabeledText label={localize(messages.projectName)} text={name} />

                    <LabeledText
                        label={localize(messages.projectType)}
                        text={localize(
                            projectType === 'Conversation' ? messages.conversationProjectType : messages.orchestrationProjectType
                        )}
                    />

                    <LabeledText
                        label={localize(messages.projectDescription)}
                        text={description || localize(messages.noValuePlaceholder)}
                    />

                    <LabeledText label={localize(messages.language)} text={cultureName} />

                    <LabeledText label={localize(messages.multipleLanguages)} text={multiLinguality} />
                </Stack>

                {projectCreationTracker.isFailed && <CollapsedErrorMessage error={projectCreationTracker.error} />}
            </CluModalScreen>

            <CluModalFooter
                onNext={onSubmit}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                isFooterDisabled={isLoading}
                activeStep={CluProjectCreationSteps.ReviewAndFinish}
                nextButtonAutomationId="createProjectButton"
            />
        </>
    );
});
